import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView, useAnimation, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ShoppingBag, Heart, MessageCircle, QrCode } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import { useWishlist } from '../../context/WishlistContext';
import { QRCodeSVG } from 'qrcode.react';

const UPI_ID = "8977093651-2@ybl";

const artworks = [
  {
    id: 1,
    title: "Fragmented Reflection of Faith",
    meaning: "This artwork presents a layered and distorted composition, blending human presence with sacred calligraphy in a fragmented visual style. The overlapping elements and digital-like disruptions create a sense of confusion and introspection, symbolizing the struggle to maintain clarity and faith in a chaotic world. Despite the distortion, glimpses of spiritual text shine through, representing resilience and the enduring connection to belief.",
    price: "₹5,800",
    size: "28x24 inches",
    images: ["/images/art1-main.jpg","/images/art1-1.jpg","/images/art1-2.jpg"]
  },
  {
    id: 2,
    title: "Al-Masjid An-Nabawi",
    meaning: "This vibrant painting depicts Al-Masjid an-Nabawi in Madinah, Saudi Arabia, one of Islam's holiest sites. The artwork captures the mosque's iconic green dome, towering minarets crowned with golden crescents, grand arched colonnades, and ornate pillars under a serene blue sky adorned with white clouds. Painted in warm, luminous tones, it radiates spiritual devotion and architectural grandeur, beautifully displayed in an elegant gold-trimmed frame.",
    price: "₹5,800",
    size: "20x23 inches",
    images: ["/images/art2-main.jpg","/images/art2-1.jpg","/images/art2-2.jpg"]
  },
  {
    id: 3,
    title: `Alif (The Whirling Soul – A Journey of Divine Love)`,
    meaning: `This exquisite diptych (two-panel) painting is a tribute to Sufi spirituality and Quranic wisdom.

Central Theme: The "Sama" or sacred dance of the Dervish.

Scriptural Focus:
• Surah Al-Fatiha
• Surah Ad-Duha`,
    price: "₹10,000",
    size: "32x18 inches",
    images: ["/images/art3-main.jpg","/images/art3-1.jpg","/images/art3-2.jpg","/images/art3-3.jpg","/images/art3-4.jpg","/images/art3-5.jpg","/images/art3-6.jpg","/images/art3-7.jpg"]
  },
  {
    id: 4,
    title: "Sacred words of Patiencce",
    meaning: "This painting features elegant Arabic calligraphy set against a warm, golden background, symbolizing peace, faith, and spiritual strength. The word “Allah” stands prominently, while the flowing script below reflects a meaningful message of patience and trust in divine wisdom. The soft textures and earthy tones create a calm and reflective mood, inviting viewers to connect deeply with the essence of devotion and inner tranquility.",
    price: "₹5,000",
    size: "22x18 inches",
    images: ["/images/art4-main.jpg","/images/art4-1.jpg","/images/art4-2.jpg","/images/art4-3.jpg"]
  },
  {
    id: 5,
    title: "Divine Light in Colors",
    meaning: "This painting beautifully blends vibrant shades of blue, green, pink, and gold to create a spiritual atmosphere. At its center, elegant calligraphy rises like a flame, symbolizing divine presence and inner peace. The textured background adds depth and emotion, while the contrast of dark and bright tones reflects the balance between struggle and enlightenment, making it a captivating expression of faith and artistic creativity.",
    price: "₹4,500",
    size: "18x16 inches",
    images: ["/images/art5-main.jpg","/images/art5-1.jpg"]
  },
  {
    id: 6,
    title: "Golden Quranic Wall Art",
    meaning: "This serene painting captures a tranquil lake embraced by towering snow-capped mountains beneath a dreamy, star-speckled sky. Lush evergreen trees frame the scene, adding depth and calmness. Gentle reflections ripple across the water, enhancing the peaceful mood. Arabic calligraphy and its translation evoke divine love, mercy, and guidance, blending spirituality with nature’s beauty, creating a harmonious expression of faith, reflection, and inner peace.",
    price: "₹4,000",
    size: "26x29 inches",
    images: ["/images/art6-main.jpg","/images/art6-1.jpg"]
  },
  {
    id: 7,
    title: "Kiswa",
    meaning: "This exquisite artwork showcases intricate Arabic calligraphy arranged in a symmetrical, ornate design, framed with elegant golden detailing. The deep black background enhances the brilliance of the gold and white inscriptions, reflecting spiritual depth and reverence. Each section is carefully structured, conveying divine words and blessings. The composition blends tradition and artistry, creating a timeless piece that inspires peace, devotion, and a deep connection to faith and sacred expression.",
    price: "₹5,000",
    size: "27.5x15",
    images: ["/images/art7-main.jpg","/images/art7-1.jpg","/images/art7-2.jpg"]
  },
  {
    id: 8,
    title: "(Hal Jazaa'ul Ihsaani Illal Ihsaan)",
    meaning: "This stunning Islamic calligraphy artwork features the Quranic verse, Is the reward for good anything but good? (Surah Ar-Rahman 55:60), rendered in bold Thuluth script on a warm ivory background. Framed by rich crimson and gold resin borders, this masterpiece beautifully blends classical Islamic tradition with contemporary mixed-media art, making it a spiritually meaningful and visually captivating decorative piece.",
    price: "₹2,000",
    size: "16x12",
    images: ["/images/art8-main.jpg","/images/art8-1.jpg","/images/art8-2.jpg","/images/art8-3.jpg","/images/art8-4.jpg"]
  },
  {
    id: 9,
    title: "Hasbunallah Wa Ni'mal Wakeel",
    meaning: "Hasbunallah Wa Ni'mal Wakeel — Allah is Sufficient for us, and He is the Best Disposer of affairs. (Surah Aal-E-Imran 3:173)A stunning hand-painted Islamic calligraphy artwork featuring bold black Arabic script on a rich emerald green background with gold accents, elegantly framed in an ornate gold frame. A perfect spiritual centerpiece for your home or office.",
    price: "₹1,500",
    size: "16x12",
    images: ["/images/art9-main.jpg","/images/art9-1.jpg","/images/art9-2.jpg","/images/art9-3.jpg"]
  },
  {
    id: 10,
    title: "The Parting of the Sea",
    meaning: "A vibrant acrylic landscape painting inspired by a Quranic verse (Surah Al-Baqarah 2:50), depicting snow-capped mountains, dark pine trees, and a flowing blue river. Arabic calligraphy and its English translation — referencing the miracle of the parting of the sea for the Children of Israel — elegantly adorn the blue sky above. The warm golden-brown earth tones beautifully contrast with the vivid greens and blues, creating a spiritually serene and breathtaking scene.",
    price: "₹2,000",
    size: "16x12",
    images: ["/images/art10-main.jpg","/images/art10-1.jpg","/images/art10-2.jpg","/images/art10-3.jpg","/images/art10-4.jpg"]
  },
  {
    id: 11,
    title: `Al-Rahman (الرحمن) 'The Most Gracious'`,
    meaning: "This striking Islamic calligraphy painting features the divine name Al-Rahman — one of Allah's 99 names, meaning 'The Most Gracious'— rendered in flowing white script against a vibrant abstract background. Bold strokes of crimson, orange, yellow, and green burst with energy beneath a deep navy canvas, symbolizing divine mercy illuminating the universe. Framed in ornate gold, it beautifully merges spiritual devotion with contemporary abstract expressionism.",
    price: "₹2,000",
    size: "16x12",
    images: ["/images/art11-main.jpg","/images/art11-1.jpg","/images/art11-2.jpg","/images/art11-3.jpg","/images/art11-4.jpg"]
  },
  {
    id: 12,
    title: "Wahy — The Divine Revelation",
    meaning: "This striking artwork features a textured white angel wing rendered in thick impasto technique against a deep navy background, framed in ornate gold. The upper left bears a Quranic verse from Surah An-Najm (53:10) — 'He revealed to His servant what He revealed' — accompanied by an Urdu couplet reflecting divine wisdom beyond human reach. A powerful fusion of sacred calligraphy and contemporary art.",
    price: "₹2,000",
    size: "16x12",
    images: ["/images/art12-main.jpg","/images/art12-1.jpg","/images/art12-2.jpg","/images/art12-3.jpg","/images/art12-4.jpg"]
  },
  {
    id: 13,
    title: "Indeed, Allah is With the Patient Ones",
    meaning: "A hand-painted artwork depicting the Palestinian flag in bold strokes of black, white, red, and green, with a landscape silhouette along the top. Centered on the white band is a Quranic verse in elegant Arabic calligraphy, followed by its Urdu translation. The piece is a heartfelt tribute to Palestinian resilience, faith, and steadfastness, elegantly framed in gold and maroon.",
    price: "₹2,000",
    size: "16x12",
    images: ["/images/art13-main.jpg","/images/art13-1.jpg","/images/art13-2.jpg","/images/art13-3.jpg","/images/art13-4.jpg","/images/art13-5.jpg"]
  },
  {
    id: 14,
    title: "The Sacred Sanctuary: Journey of Faith",
    meaning: "This breathtaking painting captures the spiritual essence of the Masjid al-Haram and the Kaaba under a celestial, vibrant sky. The artist masterfully blends architectural precision with an ethereal backdrop of jagged mountains and warm, glowing clouds. Tiny white figures symbolize pilgrims, creating a sense of movement and devotion. The rich textures and bold use of color evoke a profound feeling of peace, making this artwork a beautiful tribute to faith.",
    price: "₹20,000",
    size: "36x34",
    images: ["/images/art14-main.jpg","/images/art14-1.jpg","/images/art14-2.jpg","/images/art14-3.jpg","/images/art14-4.jpg"]
  }
];

// Image Carousel Component
const ImageCarousel = ({ images, title }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, dragFree: false });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback((e) => {
    e.stopPropagation();
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback((e) => {
    e.stopPropagation();
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="relative group/carousel">
      {/* Carousel Container */}
      <div className="overflow-hidden rounded-t-xl" ref={emblaRef}>
        <div className="flex">
          {images.map((image, index) => (
            <div 
              key={index} 
              className="flex-[0_0_100%] min-w-0"
            >
              <div className="aspect-[4/3] relative overflow-hidden bg-[#0a0a0a]">
                <img
                  src={image}
                  alt={`${title} - Image ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={scrollPrev}
        className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-all duration-300 hover:bg-black/80 hover:scale-110 z-10"
        aria-label="Previous image"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-all duration-300 hover:bg-black/80 hover:scale-110 z-10"
        aria-label="Next image"
      >
        <ChevronRight size={20} />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              emblaApi?.scrollTo(index);
            }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === selectedIndex 
                ? 'bg-[#D4AF37] w-4' 
                : 'bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>

      {/* Image Counter Badge */}
      <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm text-white text-xs font-manrope z-10">
        {selectedIndex + 1} / {images.length}
      </div>
    </div>
  );
};

// Wishlist Button Component
const WishlistButton = ({ artwork }) => {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const isWishlisted = isInWishlist(artwork.id);

  const handleClick = (e) => {
    e.stopPropagation();
    const added = toggleWishlist(artwork);
    setFeedbackText(added ? 'Added to Wishlist!' : 'Removed from Wishlist');
    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 2000);
  };

  return (
    <div className="relative">
      <motion.button
        onClick={handleClick}
        className={`absolute top-3 left-3 w-10 h-10 rounded-full backdrop-blur-sm flex items-center justify-center z-20 transition-all duration-300 ${
          isWishlisted 
            ? 'bg-[#D4AF37] text-black shadow-lg shadow-[#D4AF37]/30' 
            : 'bg-black/60 text-white hover:bg-black/80'
        }`}
        whileTap={{ scale: 0.9 }}
        data-testid={`wishlist-btn-${artwork.id}`}
        aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        <Heart 
          size={18} 
          className={isWishlisted ? 'fill-current' : ''} 
        />
      </motion.button>

      {/* Feedback Toast */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 10, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -10, x: '-50%' }}
            className="absolute top-14 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg bg-black/90 backdrop-blur-sm text-white text-xs font-manrope whitespace-nowrap z-30"
          >
            {feedbackText}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Artwork Card Component
const ArtworkCard = ({ artwork, index, onSelect }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      variants={{
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0 }
      }}
      initial="hidden"
      animate={controls}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group"
      data-testid={`artwork-card-${artwork.id}`}
    >
      <div 
        className="bg-gradient-to-b from-[#111111] to-[#0a0a0a] rounded-xl overflow-hidden border border-[#1a1a1a] hover:border-[#D4AF37]/30 transition-all duration-500 hover:shadow-[0_8px_40px_rgba(212,175,55,0.15)] cursor-pointer relative"
        onClick={() => onSelect(artwork)}
      >
        {/* Wishlist Button */}
        <WishlistButton artwork={artwork} />

        {/* Image Carousel */}
        <ImageCarousel images={artwork.images} title={artwork.title} />

        {/* Card Content */}
        <div className="p-5">
          {/* Title */}
          <h3 className="font-cinzel text-lg text-white mb-2 group-hover:text-[#D4AF37] transition-colors duration-300 line-clamp-1">
            {artwork.title}
          </h3>

          {/* Size Badge */}
          <div className="inline-block px-2.5 py-1 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] mb-3">
            <span className="font-manrope text-xs text-gray-400">{artwork.size}</span>
          </div>

          {/* Description */}
          <p className="font-manrope text-sm text-gray-400 leading-relaxed mb-4 line-clamp-2">
            {artwork.meaning}
          </p>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-[#2a2a2a] to-transparent mb-4" />

          {/* Price and CTA */}
          <div className="flex items-center justify-between">
            <div>
              <span className="font-manrope text-xs text-gray-500 block">Price</span>
              <span className="font-cinzel text-2xl font-bold text-[#D4AF37]">
                {artwork.price}
              </span>
            </div>
            <button 
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] font-manrope text-sm font-medium hover:bg-[#D4AF37] hover:text-black transition-all duration-300"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(artwork);
              }}
            >
              <ShoppingBag size={16} />
              <span>View</span>
            </button>
          </div>

          {/* Buy on WhatsApp Button */}
          <button
            className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#25D366] text-white font-manrope text-sm font-medium hover:bg-[#22c55e] transition-all duration-300"
            onClick={(e) => {
              e.stopPropagation();
              const message = encodeURIComponent(`Hello, I'm interested in buying this artwork: ${artwork.title}`);
              window.open(`https://wa.me/919381690689?text=${message}`, '_blank');
            }}
            data-testid={`whatsapp-buy-${artwork.id}`}
          >
            <MessageCircle size={16} />
            <span>Buy on WhatsApp</span>
          </button>

          {/* Scan & Pay UPI Section */}
          <UPIPaymentSection artwork={artwork} />
        </div>
      </div>
    </motion.div>
  );
};

// UPI Payment Section with QR Code
const UPIPaymentSection = ({ artwork }) => {
  const [showQR, setShowQR] = useState(false);
  
  // Extract numeric price for UPI
  const priceAmount = artwork.price.replace(/[₹,]/g, '').trim();
  const upiLink = `upi://pay?pa=${UPI_ID}&pn=Ethereal%20Calligraphy&am=${priceAmount}&cu=INR&tn=${encodeURIComponent(artwork.title)}`;

  return (
    <div className="mt-3 p-3 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a]">
      <div className="flex items-center justify-between mb-2">
        <p className="font-manrope text-xs text-gray-400">Scan & Pay via UPI</p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowQR(!showQR);
          }}
          className="flex items-center gap-1 text-[#D4AF37] text-xs font-manrope hover:text-[#F4C430] transition-colors"
          data-testid={`toggle-qr-${artwork.id}`}
        >
          <QrCode size={14} />
          <span>{showQR ? 'Hide QR' : 'Show QR'}</span>
        </button>
      </div>
      
      <p className="font-manrope text-sm text-[#D4AF37] font-medium">{UPI_ID}</p>
      
      {/* QR Code */}
      <AnimatePresence>
        {showQR && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3 flex flex-col items-center">
              <div className="bg-white p-3 rounded-lg shadow-lg shadow-[#D4AF37]/10">
                <QRCodeSVG
                  value={upiLink}
                  size={120}
                  level="H"
                  includeMargin={false}
                  data-testid={`upi-qr-${artwork.id}`}
                />
              </div>
              <p className="mt-2 font-manrope text-xs text-gray-500 text-center">
                Scan with any UPI app
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const GallerySection = ({ onSelectArtwork }) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  // Transform artworks to include images array if not present
  const processedArtworks = artworks.map(artwork => ({
    ...artwork,
    images: artwork.images || [artwork.image],
    image: artwork.images?.[0] || artwork.image
  }));

  return (
    <section id="gallery" className="py-20 md:py-28 relative" ref={sectionRef} data-testid="gallery-section">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#080808] to-[#050505] pointer-events-none" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <span className="font-manrope text-sm text-[#D4AF37] uppercase tracking-[0.2em] mb-4 block">
            Our Collection
          </span>
          <h2 className="font-cinzel text-4xl md:text-5xl text-gold-gradient mb-5">
            The Collection
          </h2>
          <div className="w-20 h-0.5 bg-gradient-to-r from-[#AA8C2C] via-[#D4AF37] to-[#AA8C2C] mx-auto mb-6" />
          <p className="font-manrope text-gray-400 max-w-2xl mx-auto text-base md:text-lg">
            Each piece is a unique expression of spiritual devotion, handcrafted with precision and love.
          </p>
        </motion.div>

        {/* Gallery Grid - Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 md:gap-8">
          {processedArtworks.map((artwork, index) => (
            <ArtworkCard
              key={artwork.id}
              artwork={artwork}
              index={index}
              onSelect={onSelectArtwork}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-14"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
        >
          <p className="font-manrope text-gray-500 mb-4">
            Looking for something specific?
          </p>
          <a 
            href="#contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] font-manrope text-sm hover:bg-[#D4AF37]/10 transition-all duration-300"
          >
            Request Custom Artwork
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export { artworks };
export default GallerySection;
